// This file contains route definitions to be used in react/ts
// These are manually added as needed

// Utils & Prefixes
import { ADMIN_PREFIX, BROWSER_API_PREFIX, WEB_PREFIX } from './constants';

const page = (slug: string) => `${WEB_PREFIX}${slug}`;

const routes = {
  /*
  |--------------------------------------------------------------------------
  | API
  |--------------------------------------------------------------------------
  */

  // Sellers
  API_SELLER_POST: `${BROWSER_API_PREFIX}sellers`,
  apiSellerGet: (id: number) => `${BROWSER_API_PREFIX}sellers/${id}`,
  apiSellerUpdate: (id: number) => `${BROWSER_API_PREFIX}sellers/${id}`,
  apiSellerProfilePhotoPost: (id: number) => `${BROWSER_API_PREFIX}sellers/${id}/profile-photo-image`,
  apiSellerGalleryPost: (id: number) => `${BROWSER_API_PREFIX}sellers/${id}/gallery-photo`,
  adminSellerRevisionsGet: (id: number|string) => `${ADMIN_PREFIX}seller-profiles/${id}/revisions`,

  // Seller Contacts
  API_SELLER_CONTACTS_POST: `${BROWSER_API_PREFIX}seller-contacts`,
  apiSellerContactsUpdate: (id: number | string) => `${BROWSER_API_PREFIX}seller-contacts/${id}`,
  apiSellerContactsDelete: (id: number | string) => `${BROWSER_API_PREFIX}seller-contacts/${id}`,

  // Payment Profiles
  API_PAYMENT_PROFILE: `${BROWSER_API_PREFIX}payment-methods`,
  apiPaymentProfileUpdate: (id: string) => `${BROWSER_API_PREFIX}payment-methods/${id}`,
  apiPaymentProfileDelete: (id: string) => `${BROWSER_API_PREFIX}payment-methods/${id}`,
  API_GET_STRIPE_CLIENT_SECRET: `${BROWSER_API_PREFIX}payment-methods/stripe-client-secret`,
  API_CANCEL_STRIPE_SETUP_INTENT: `${BROWSER_API_PREFIX}payment-methods/cancel-stripe-setup-intent`,

  // Users
  apiUserPut: (id: number) => `${BROWSER_API_PREFIX}users/${id}`,
  adminUserRevisionsGet: (id: number|string) => `${ADMIN_PREFIX}users/${id}/revisions`,

  // Listings
  apiFavoritePost: (type: string, id: number) => `${BROWSER_API_PREFIX}listing/${type}/${id}/favorite`,
  apiUnfavoritePost: (type: string, id: number) => `${BROWSER_API_PREFIX}listing/${type}/${id}/unfavorite`,
  apiSearchPreview: (type: string) => `${BROWSER_API_PREFIX}listing/${type}/search-preview`,

  // Breeds
  API_BREED_POST: `${BROWSER_API_PREFIX}breeds`,
  API_BREED_INDEX: `${BROWSER_API_PREFIX}breeds`,
  apiBreedUpdate: (id: number) => `${BROWSER_API_PREFIX}breeds/${id}`,
  apiBreedGet: (id: number) => `${BROWSER_API_PREFIX}breed/${id}`,
  apiUploadBreedMedia: (id: number) => `${BROWSER_API_PREFIX}breed/${id}/gallery-photo`,
  apiBreedDelete: (id: number) => `${BROWSER_API_PREFIX}breeds/${id}`,
  apiBreedBulkSort: `${BROWSER_API_PREFIX}breeds/bulk-sort-popular`,

  // Content
  apiGetPossibleAuthors: (contentType: string) => `${BROWSER_API_PREFIX}authors/${contentType}`,

  // TFA
  API_TFA_GENERATE_EMAIL_TOKEN: `${BROWSER_API_PREFIX}user/email/verify/new`,
  API_TFA_GENERATE_CALL_TOKEN: `${BROWSER_API_PREFIX}user/phone/verify/new/via-call`,
  API_TFA_GENERATE_SMS_TOKEN: `${BROWSER_API_PREFIX}user/phone/verify/new/via-sms`,
  API_TFA_GENERATE_SMS_TOKEN_AND_UPDATE_PHONE: `${BROWSER_API_PREFIX}user/phone/verify-and-update/new/via-sms`,
  API_TFA_VERIFY_TOKEN: `${BROWSER_API_PREFIX}user/phone/verify`,
  TFA_RESEND_CODE: `${BROWSER_API_PREFIX}user/tfa/verify/new`,

  // Media
  apiMediaUpdate: (id: number) => `${BROWSER_API_PREFIX}media/${id}`,
  apiMediaDelete: (id: number) => `${BROWSER_API_PREFIX}media/${id}`,

  // Misc
  API_REGISTER: `${BROWSER_API_PREFIX}register`,
  API_LOGIN_POST: `${BROWSER_API_PREFIX}login`,
  API_FORGOT_PASSWORD_POST: `${BROWSER_API_PREFIX}password/email`,

  // Seller Contact
  API_SELLER_CONTACT_FORM_INFO: (
    sellerContactId: number,
    listingId?: number|null|undefined,
    listingType?: string|null|undefined,
  ) => {
    return `${BROWSER_API_PREFIX}seller-contact/${sellerContactId}/seller-contact-form-info${
      listingId && listingType ? `?listingId=${listingId}&listingType=${listingType}` : ''}`;
  },

  API_SELLER_CONTACT_POST: `${BROWSER_API_PREFIX}form-submissions/seller-contact`,

  // Basic Contact
  API_BASIC_CONTACT_POST: `${BROWSER_API_PREFIX}form-submissions/basic-contact`,

  // Account Deletion Request
  API_ACCOUNT_DELETION_REQUEST_POST: `${BROWSER_API_PREFIX}form-submissions/account-deletion-request`,

  // Vouchers
  API_VOUCHER_POST: `${BROWSER_API_PREFIX}vouchers`,

  /*
  |--------------------------------------------------------------------------
  | Web
  |--------------------------------------------------------------------------
  */

  // Pages
  TERMS_OF_SERVICE_GET: page('terms'),
  PRIVACY_POLICY_GET: page('privacy-policy'),

  // Search
  webSearch: (query?: string) => `${WEB_PREFIX}puppy-search${query ? `?query=${query}` : ''}`,
  // TODO: Add query to stud search; cf. https://schweb.atlassian.net/browse/LPBUGS-1315
  webStudSearch: (query?: string) => `${WEB_PREFIX}breeds/type/stud`,
  webBreederSearch: (query?: string) => `${WEB_PREFIX}breeders-search`,

  // Breeds
  WEB_BREEDS_INDEX: `${WEB_PREFIX}breeds`,
  webBreedStateSearch: (breed:string, type:string, state:string) => `${WEB_PREFIX}breeds/${breed}/${type}?state=${state}`,

  // TFA
  TFA_FORM: `${WEB_PREFIX}tfa`,

  // Login
  WEB_LOGIN: `${WEB_PREFIX}login`,

  /*
  |--------------------------------------------------------------------------
  | Admin
  |--------------------------------------------------------------------------
  */

  ADMIN_INDEX: `${ADMIN_PREFIX}`,

  // Sellers
  adminSellerEdit: (slug: string) => `${ADMIN_PREFIX}sellers/${slug}/edit`,

  // Seller Contacts
  adminSellerContactsIndex: (slug: string) => `${ADMIN_PREFIX}sellers/${slug}/contacts`,

  // Listings
  LISTINGS_ALL_BULK_UPDATE: `${BROWSER_API_PREFIX}listing/all/bulk-update`,
  LISTINGS_ALL_BULK_DELETE: `${BROWSER_API_PREFIX}listing/all/bulk-delete`,
  renewSingleListing: (type: string, id: number) => `${BROWSER_API_PREFIX}listing/${type}/renew-single/${id}`,

  // Account Deletion Request
  ACCOUNT_DELETION_REQUEST_INDEX: `${ADMIN_PREFIX}form-submissions?type=account_deletion_request`,
  adminDeleteFormsAccountDeletionRequest: (id: number) => `${BROWSER_API_PREFIX}form-submissions/account-deletion-request/${id}`,

  // Breeds
  adminEditBreed: (slug: string) => `${ADMIN_PREFIX}breeds/${slug}/edit`,

};

export default routes;
